import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  MINI_STAT_COUNT_GRADIENT,
  MINI_STAT_LOSS_GRADIENT,
  MINI_STAT_OPEN_GRADIENT,
  MINI_STAT_WIN_GRADIENT,
} from "@/utils/miniStatGradient";
import { getAdminOverview, type AdminOverview } from "../../../services/adminOverviewApi";
import { liveSectionTitleClassName, liveSmallTextClassName } from "../LiveStrategies/liveStrategiesUi";
import TradeLogMiniStatistics from "../TradeLog/components/TradeLogMiniStatistics";

const n = (v: number | null | undefined, digits = 0) =>
  v == null ? "–" : v.toLocaleString(undefined, { maximumFractionDigits: digits });
const usd = (v: number | null | undefined) => (v == null ? "–" : `$${n(v, 2)}`);

type Tile = { title: string; amount: string; secondary?: string; tooltip?: string; gradient?: string };

function Section({ title, tiles }: { title: string; tiles: Tile[] }) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className={liveSectionTitleClassName}>{title}</h2>
      <div data-mini-stats-grid="" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((t) => (
          <TradeLogMiniStatistics
            key={t.title}
            title={t.title}
            amount={t.amount}
            secondaryAmount={t.secondary ?? null}
            percentage={null}
            showPercentage={false}
            backgroundGradient={t.gradient ?? MINI_STAT_COUNT_GRADIENT}
            tooltip={t.tooltip}
            fitAmount
          />
        ))}
      </div>
    </section>
  );
}

/** What's happening right now: users, plans, money, AI and data usage, and vendor headroom. */
export default function AdminOverviewPanel() {
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState<AdminOverview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getAdminOverview(days));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load the overview.");
    } finally {
      setLoading(false);
    }
  }, [days]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const p = data?.period;
  const subs = p?.subscriptions ?? {};
  const active = (tier: string) => (subs[`${tier}:active`] ?? 0) + (subs[`${tier}:trialing`] ?? 0);
  const plus = active("plus") + active("tier1_dashboard");
  const paid = active("standard") + plus + active("pro");
  const td = data?.system.twelvedata;
  const budgetPct = data?.system.ai_budget_used_pct ?? null;

  return (
    <div className="flex flex-col gap-8" data-admin-overview="">
      <div className="flex flex-wrap items-center gap-2">
        {[7, 30, 90].map((d) => (
          <Button key={d} type="button" size="sm" data-font="primary" className="shadow-none"
            variant={d === days ? "outline-brand" : "outline-gray"} onClick={() => setDays(d)}>
            <span className="normal-case tracking-normal">Last {d} days</span>
          </Button>
        ))}
        <Button type="button" size="sm" variant="outline-gray" data-font="primary" className="shadow-none"
          onClick={() => void load()} disabled={loading}>
          <span className="normal-case tracking-normal">{loading ? "Loading…" : "Refresh"}</span>
        </Button>
        {data ? <span className={liveSmallTextClassName}>As of {new Date(data.as_of).toLocaleString()}</span> : null}
      </div>
      {error ? <p className="m-0 text-sm text-red-400">{error}</p> : null}

      {p ? (
        <>
          <Section title="Users" tiles={[
            { title: "Total users", amount: n(p.users.total), secondary: `${n(p.users.admins)} admins` },
            { title: "New (7 / 30 days)", amount: `${n(p.users.new_7d)} / ${n(p.users.new_30d)}` },
            { title: "Active this week", amount: n(p.users.active_7d), secondary: "signed in, last 7 days" },
            { title: "Paid / Free", amount: `${n(paid)} / ${n(Math.max(0, p.users.total - paid))}` },
          ]} />
          <Section title="Revenue" tiles={[
            {
              title: "MRR", amount: data?.revenue.available ? usd(data.revenue.mrr_usd) : "–",
              secondary: data?.revenue.available ? `Stripe ${data.revenue.mode} · ${n(data.revenue.subscriptions)} subs` : "Stripe unavailable",
              gradient: MINI_STAT_WIN_GRADIENT,
            },
            { title: "Standard / Plus / Pro", amount: `${active("standard")} / ${plus} / ${active("pro")}` },
            {
              title: "Cancelling", amount: n(p.cancelling), secondary: "at period end",
              gradient: p.cancelling ? MINI_STAT_OPEN_GRADIENT : MINI_STAT_COUNT_GRADIENT,
            },
            {
              title: `Packs sold (${days}d)`, amount: n(p.packs.sold),
              secondary: Object.entries(p.packs.sold_by_pack).map(([k, v]) => `${k.replace("pack_", "$")}×${v}`).join(" · ") || undefined,
              gradient: MINI_STAT_WIN_GRADIENT,
            },
          ]} />
          <Section title={`TES, last ${days} days`} tiles={[
            { title: "Prompts", amount: n(p.ai.turns), secondary: `${n(p.ai.users)} users` },
            { title: "Credits used", amount: n(p.ai.credits),
              secondary: Object.entries(p.ai.by_bucket).map(([k, v]) => `${k} ${n(v)}`).join(" · ") || undefined },
            { title: "AI cost", amount: usd(p.ai.cost_usd),
              secondary: p.ai.turns ? `${usd(p.ai.cost_usd / p.ai.turns)} a prompt` : undefined },
            { title: "Credits owed", amount: n(p.packs.credits_outstanding), secondary: "bought, not used yet",
              tooltip: "Credits users bought and haven't spent (12-month expiry)." },
          ]} />
          <Section title="Lab & Launchpad" tiles={[
            { title: `Backtests (${days}d)`, amount: n(p.backtests.count), secondary: `${n(p.backtests.users)} users` },
            { title: "Strategies running", amount: n(p.launchpad.running), secondary: `${n(p.launchpad.users)} users` },
            { title: "On a broker", amount: n(p.launchpad.running_live), secondary: "co-pilot or autopilot" },
            { title: "Add-on slots sold", amount: `${n(p.addons.live_slot)} live`, secondary: `${n(p.addons.paper_slots)} paper packs` },
          ]} />
          <Section title="System headroom" tiles={[
            {
              title: "AI cost this month", amount: usd(data?.system.ai_cost_month_usd),
              secondary: data?.system.ai_budget_month_usd
                ? `${n(budgetPct, 1)}% of ${usd(data.system.ai_budget_month_usd)}` : "no budget set",
              gradient: budgetPct != null && budgetPct >= 90 ? MINI_STAT_LOSS_GRADIENT
                : budgetPct != null && budgetPct >= 70 ? MINI_STAT_OPEN_GRADIENT : MINI_STAT_COUNT_GRADIENT,
              tooltip: "Set AI_MONTHLY_BUDGET_USD on the API to track spend against a budget.",
            },
            { title: "Prompts this month", amount: n(data?.month.ai.turns), secondary: `${n(data?.month.ai.credits)} credits` },
            {
              title: "Twelve Data / min", amount: td?.available ? `${n(td.current_usage)} / ${n(td.plan_limit)}` : "–",
              secondary: td?.available ? `plan: ${td.plan_category}` : "unavailable",
              gradient: td?.available && td.plan_limit && (td.current_usage ?? 0) / td.plan_limit >= 0.8
                ? MINI_STAT_OPEN_GRADIENT : MINI_STAT_COUNT_GRADIENT,
            },
            { title: "Backtests this month", amount: n(data?.month.backtests.count) },
          ]} />
        </>
      ) : null}
      {!p && loading ? <p className={cn(liveSmallTextClassName, "m-0")}>Loading…</p> : null}
    </div>
  );
}
